<template>
  <div :class="cn('flex h-96 w-full gap-4 squircle-40', props.class)">
    <MotionPresence multiple>
      <Motion
      @mouseenter="() => {
        const id = item.id?.toString() || '';
        console.log('[Gallery] Motion hover enter:', id);
        emit('imageHover', id);
      }"
      @mouseleave="() => {
        console.log('[Gallery] Motion hover leave');
        emit('imageHover', null);
      }"
        v-for="(item, index) in visibleItems"
        :key="`gallery-${item.id || item.image}`"
        v-show="item.visible"
        :initial="{ opacity: 1 }"
        :animate="{ 
          opacity: 1, 
          transition: {
            type: 'spring',
            stiffness: 100,
            damping: 15,
            delay: index * 0.01
          }
        }"
        :exit="{ 
          opacity: 0, 
          transition: {
            duration: 0.3,
            ease: 'easeInOut'
          }
        }"
        :class="[
          'relative flex h-full flex-1 cursor-pointer overflow-hidden rounded-xl transition-all duration-500 ease-in-out hover:flex-[3]',
          // Container query responsive classes to show different numbers of items
          {
            // Always show first 2 items (base case for narrow containers)
            'block': index < 2,
            // Show 3rd item on small containers and up
            'hidden @sm:block': index === 2,
            // Show 4th item on medium containers and up
            'hidden @md:block': index === 3,
            // Show 5th item on large containers and up  
            'hidden @lg:block': index === 4,
            // Show 6th item on extra large containers and up
            'hidden @xl:block': index === 5,
            // Show 7th item on 2xl containers and up
            'hidden @2xl:block': index === 6,
            // Show 8th item on 3xl containers and up
            'hidden @3xl:block': index === 7,
            // Hide any items beyond 8
            'hidden': index >= 8
          },
          { '': item.highlighted }
        ]"
        @click="() => emit('imageClick', item)"
      >
        <img
          :class="[
            'relative h-full w-full object-cover transition-all duration-200',
            {
              '!border-[4px] border-primary shadow-lg': item.hovered
            }
          ]"
          :style="{
            '--tw-ring-color': item.hovered ? 'hsl(var(--primary))' : undefined,
            '--tw-ring-width': item.hovered ? '3px' : undefined,
            '--tw-ring-offset-width': item.hovered ? '2px' : undefined,
            'box-shadow': item.hovered ? 'var(--tw-ring-inset) 0 0 0 calc(3px + var(--tw-ring-offset-width)) hsl(var(--primary))' : undefined,
            'border-radius': item.hovered ? 
              (index === 0 ? '42px 12px 12px 42px !important' : // First item hovered: large left radius
               index === visibleItems.length - 1 ? '12px 42px 42px 12px !important' : // Last item hovered: large right radius
               '14px !important') : // Middle items hovered: standard radius
              '12px !important' // All items when not hovered: standard radius
          }"
          :src="getOptimizedImageUrl(item)"
          :alt="item.alt || item.title || 'Image'"
          loading="lazy"
          decoding="async"
        />
        <!-- Text overlay with stable CSS transitions -->
        <div
          v-if="item.title || item.subtitle || item.metadata"
          class="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"
        >
          <div class="absolute bottom-0 left-0 right-0 p-4 text-white">
            <h3 v-if="item.title" class="font-medium text-2xl mb-1 truncate">{{ item.title }}</h3>
            <p v-if="item.subtitle" class="text-xs text-white/80 mb-1 truncate">{{ typeof item.subtitle === 'string' ? item.subtitle.replace(/[\[\]\"\']+/g, ' ') : item.subtitle }}</p>
            <div v-if="item.metadata" class="text-xs text-white/60 space-y-0.5">
              <div v-for="meta in item.metadata.filter(meta => meta.label !== 'Distance')" :key="meta.label" class="flex justify-between">
                <span>{{ meta.label }}:</span>
                <span>{{ meta.value }}</span>
              </div>
            </div>
            <a v-if="item.uri && item.source" 
               :href="item.uri" 
               target="_blank" 
               rel="noopener noreferrer"
               class="text-xs text-white/90 hover:text-white hover:underline mt-2 inline-flex items-center"
               @click.stop>
              View on {{ item.source }}
              <Icon name="lucide:external-link" class="w-3 h-3 ml-1" />
            </a>
          </div>
        </div>
      </Motion>
    </MotionPresence>
  </div>
</template>

<script lang="ts" setup>
import type { HTMLAttributes } from 'vue';
import { cn } from '@/lib/utils';
import { Motion, MotionPresence } from '@oku-ui/motion'
import { ref, watch, nextTick } from 'vue'

interface GalleryItem {
  id?: string | number;
  image: string;
  alt?: string;
  title?: string;
  subtitle?: string;
  metadata?: Array<{ label: string; value: string }>;
  highlighted?: boolean;
  hovered?: boolean;
  source?: 'iNaturalist' | 'ALA' | string;
  uri?: string;
}

useSquircleObserver()

interface Props {
  items: GalleryItem[];
  class?: HTMLAttributes['class'];
  resolution?: 'square' | 'small' | 'medium' | 'large'; // Unified resolution (deprecated in favor of source-specific)
  iNaturalistResolution?: 'square' | 'small' | 'medium' | 'large'; // iNaturalist-specific resolution
  alaResolution?: 'thumbnail' | 'small' | 'medium' | 'large'; // ALA-specific resolution
}

// Note: Parent component now provides unified resolution mapping through
// iNaturalistResolution and alaResolution computed properties
const props = withDefaults(defineProps<Props>(), {
  resolution: 'large',
  iNaturalistResolution: 'large',
  alaResolution: 'large'
});

// Define emits for hover events
const emit = defineEmits<{
  imageHover: [id: string | null]
  imageClick: [item: GalleryItem]
}>();

interface VisibleGalleryItem extends GalleryItem {
  visible: boolean;
}

const visibleItems = ref<VisibleGalleryItem[]>([]);

// Initialize visible items
visibleItems.value = props.items.map(item => ({ ...item, visible: true }));

// Watch for changes in items prop and handle exit animations
watch(() => props.items, async (newItems, oldItems) => {
  // Quick check if items are actually different to avoid unnecessary updates
  if (oldItems && newItems.length === oldItems.length) {
    const sameItems = newItems.every((newItem, index) => {
      const oldItem = oldItems[index];
      return (newItem.id || newItem.image) === (oldItem?.id || oldItem?.image) &&
             newItem.title === oldItem?.title &&
             newItem.hovered === oldItem?.hovered &&
             newItem.highlighted === oldItem?.highlighted;
    });
    
    if (sameItems) {
      // Just update the existing items in place to avoid re-rendering
      visibleItems.value.forEach((visibleItem, index) => {
        if (newItems[index]) {
          Object.assign(visibleItem, newItems[index], { visible: true });
        }
      });
      return;
    }
  }
  
  const newItemMap = new Map(newItems.map(item => [item.id || item.image, item]));
  const currentItemMap = new Map(visibleItems.value.map(item => [item.id || item.image, item]));
  
  // Find items to remove (exist in current but not in new)
  const itemsToRemove = visibleItems.value.filter(
    item => !newItemMap.has(item.id || item.image)
  );
  
  // Find items to add (exist in new but not in current)
  const itemsToAdd = newItems.filter(
    item => !currentItemMap.has(item.id || item.image)
  );
  
  // Hide items that should be removed (triggers exit animation)
  itemsToRemove.forEach(item => {
    item.visible = false;
  });
  
  // Wait for exit animations to complete before removing from array
  if (itemsToRemove.length > 0) {
    await new Promise(resolve => setTimeout(resolve, 300)); // Match exit transition duration
  }
  
  // Create new visible items array maintaining the order from newItems
  const updatedVisibleItems: VisibleGalleryItem[] = [];
  
  newItems.forEach(newItem => {
    const itemKey = newItem.id || newItem.image;
    const existingItem = currentItemMap.get(itemKey);
    
    if (existingItem) {
      // Item exists, update its data but keep it visible (no transition for position changes)
      updatedVisibleItems.push({ ...newItem, visible: true });
    } else {
      // New item, add it (will trigger entrance animation)
      updatedVisibleItems.push({ ...newItem, visible: true });
    }
  });
  
  visibleItems.value = updatedVisibleItems;
}, { deep: true });

// Function to optimize image URLs based on source and resolution
const getOptimizedImageUrl = (item: GalleryItem): string => {
  const source = item.source?.toLowerCase();
  let imageUrl = item.image;
  
  if (!imageUrl) return '';
  
  if (source === 'inaturalist') {
    // For iNaturalist, replace any existing size with the desired resolution
    const resolution = props.iNaturalistResolution || props.resolution;
    
    // Ensure we have a valid iNaturalist photo URL
    if (imageUrl.includes('inaturalist') || imageUrl.includes('staticflickr')) {
      imageUrl = imageUrl.replace(/\/(square|small|medium|large)\./, `/${resolution}.`);
      
      // If no size was found to replace, append the size
      if (!imageUrl.includes(`/${resolution}.`)) {
        // Handle different URL patterns
        if (imageUrl.includes('staticflickr')) {
          // For Flickr URLs, modify the size parameter
          imageUrl = imageUrl.replace(/_[a-z]\.jpg$/i, `_${resolution === 'square' ? 's' : resolution === 'small' ? 'm' : resolution === 'medium' ? 'z' : 'b'}.jpg`);
        }
      }
    }
  } else if (source === 'ala') {
    // For ALA, we'll handle this through URL parameters or different endpoints
    const resolution = props.alaResolution || props.resolution;
    
    // Check if it's an ALA image URL and modify accordingly
    if (imageUrl.includes('api.ala.org.au/images') || imageUrl.includes('biocache.ala.org.au') || imageUrl.includes('ala.org.au')) {
      try {
        const url = new URL(imageUrl);
        
        // For ALA images, use different size parameters based on resolution
        if (resolution === 'thumbnail') {
          url.searchParams.set('size', 'thumbnail');
        } else if (resolution === 'small') {
          url.searchParams.set('size', 'small');
        } else if (resolution === 'medium') {
          url.searchParams.set('size', 'medium');
        }
        // For 'large', don't add size parameter (default)
        
        imageUrl = url.toString();
      } catch (error) {
        // If URL parsing fails, return original
        console.warn('Failed to parse ALA image URL:', imageUrl);
      }
    }
  }
  
  return imageUrl;
};
</script>
